import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users } from 'lucide-react';
import { useSharedUsers } from '@/hooks/useShareCaso';
import { useSharedCasoGroups } from '@/hooks/useSharedCasoGroups';

interface SharedCasoMembersProps {
  casoId: string;
  maxVisible?: number;
  size?: 'sm' | 'md' | 'lg';
}

export function SharedCasoMembers({ 
  casoId, 
  maxVisible = 3,
  size = 'md' 
}: SharedCasoMembersProps) {
  const { data: users, isLoading: loadingUsers } = useSharedUsers(casoId);
  const { data: groups, isLoading: loadingGroups } = useSharedCasoGroups(casoId);

  const isLoading = loadingUsers || loadingGroups;

  if (isLoading) {
    return (
      <div className="caso-share-avatars">
        {Array.from({ length: maxVisible }).map((_, i) => (
          <div 
            key={i} 
            className="caso-share-avatar-skeleton" 
          />
        ))}
      </div>
    );
  }

  const totalItems = (groups?.length || 0) + (users?.length || 0);
  
  if (totalItems === 0) {
    return null;
  }

  const groupsToShow = groups?.slice(0, Math.min(groups.length, maxVisible)) || [];
  const remainingSlots = maxVisible - groupsToShow.length;
  const usersToShow = users?.slice(0, remainingSlots) || [];
  const remainingCount = totalItems - groupsToShow.length - usersToShow.length;

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email[0].toUpperCase();
  };

  return (
    <TooltipProvider>
      <div className="caso-share-avatars">
        {/* Exibir Grupos */}
        {groupsToShow.map((group) => (
          <Tooltip key={`group-${group.id}`}>
            <TooltipTrigger asChild>
              <div className={`caso-share-avatar ${sizeClasses[size]} bg-primary/10 border-primary/20`}>
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="h-4 w-4 text-primary" />
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">{group.name}</p>
              <p className="text-xs text-muted-foreground">
                {group.member_count} {group.member_count === 1 ? 'membro' : 'membros'}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}

        {/* Exibir Usuários */}
        {usersToShow.map((member) => (
          <Tooltip key={`user-${member.id}`}>
            <TooltipTrigger asChild>
              <div className={`caso-share-avatar ${sizeClasses[size]}`}>
                <Avatar className="w-full h-full">
                  {member.avatar_url && (
                    <AvatarImage src={member.avatar_url} alt={member.display_name || member.email} />
                  )}
                  <AvatarFallback>
                    {getInitials(member.display_name, member.email)}
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">{member.display_name || member.email}</p>
            </TooltipContent>
          </Tooltip>
        ))}

        {/* Contador de +N restantes */}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`caso-share-avatar caso-share-avatar-more ${sizeClasses[size]}`}>
                +{remainingCount}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {remainingCount} {remainingCount === 1 ? 'item adicional' : 'itens adicionais'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
